import { traduzirErroSupabase } from '@octawash/shared';
import { useQuery } from '@tanstack/react-query';
import { AnimatePresence, motion } from 'framer-motion';
import { ArrowLeft, CarFront, CheckCircle2, Loader2, Palette, Sparkles, Tag } from 'lucide-react';
import { useMemo, useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { toast } from 'sonner';

import { Button } from '@/components/ui/button';
import { Card, CardContent } from '@/components/ui/card';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { useAuth } from '@/lib/auth-context';
import { supabase } from '@/lib/supabase';
import { cn } from '@/lib/utils';

// Cast — tab_marcas_veiculo / tab_modelos_veiculo não estão em database.types ainda
// eslint-disable-next-line @typescript-eslint/no-explicit-any
const sb = supabase as any;

interface Marca {
  seq_marca: number;
  nom_marca: string;
}
interface Modelo {
  seq_modelo: number;
  seq_marca: number;
  nom_modelo: string;
}

/**
 * Normaliza qualquer entrada de placa (formato livre, com ou sem hifen)
 * pro padrao alfanumerico maiusculo. Aceita ABC-1234, ABC1234, abc1d23.
 */
function normalizarPlaca(s: string) {
  return s.toUpperCase().replace(/[^A-Z0-9]/g, '');
}

/**
 * Detecta se uma placa é Mercosul (ABC1D23) ou antiga (ABC1234).
 * Retorna 'INVALIDA' se não bate em nenhum dos dois.
 */
function classificarPlaca(s: string): 'MERCOSUL' | 'ANTIGA' | 'INVALIDA' {
  const n = normalizarPlaca(s);
  if (/^[A-Z]{3}[0-9][A-Z][0-9]{2}$/.test(n)) return 'MERCOSUL';
  if (/^[A-Z]{3}[0-9]{4}$/.test(n)) return 'ANTIGA';
  return 'INVALIDA';
}

// Erro -> mensagem amigável: usa o tradutor central do packages/shared
const mensagemErro = traduzirErroSupabase;

const CORES_POPULARES: Array<{ nome: string; bg: string; ring: string }> = [
  { nome: 'Branco', bg: '#FAFAFA', ring: 'ring-zinc-300' },
  { nome: 'Preto', bg: '#0F0F11', ring: 'ring-zinc-900' },
  { nome: 'Prata', bg: '#C8CDD2', ring: 'ring-zinc-400' },
  { nome: 'Cinza', bg: '#6B7280', ring: 'ring-zinc-500' },
  { nome: 'Vermelho', bg: '#DC2626', ring: 'ring-rose-500' },
  { nome: 'Azul', bg: '#1D4ED8', ring: 'ring-blue-600' },
  { nome: 'Verde', bg: '#15803D', ring: 'ring-emerald-600' },
  { nome: 'Marrom', bg: '#78350F', ring: 'ring-amber-900' },
  { nome: 'Bege', bg: '#D6CCB0', ring: 'ring-stone-400' },
  { nome: 'Amarelo', bg: '#EAB308', ring: 'ring-yellow-500' },
];

/**
 * PlacaPreview — visual fiel da placa brasileira (Mercosul tem faixa azul
 * superior, antiga é toda cinza). Espelha o que o cliente vê no carro.
 */
function PlacaPreview({
  texto,
  padrao,
}: {
  texto: string;
  padrao: 'MERCOSUL' | 'ANTIGA' | 'INVALIDA';
}) {
  const display = texto.length > 0 ? texto : 'ABC0000';
  const formatado = padrao === 'INVALIDA' ? display : formatarPlacaDisplay(display, padrao);
  const isMercosul = padrao === 'MERCOSUL';
  const placeholder = texto.length === 0;
  return (
    <div
      className={cn(
        'relative mx-auto h-[68px] w-[180px] overflow-hidden rounded-md border-2 border-zinc-800 bg-white text-zinc-900 shadow-md',
        placeholder && 'opacity-50',
      )}
      aria-hidden
    >
      {/* Faixa superior (Mercosul = azul, antiga = vazia) */}
      {isMercosul ? (
        <div className="flex h-3 items-center justify-between bg-blue-700 px-1.5 text-[6px] font-bold uppercase text-white">
          <span>BR</span>
          <span className="tracking-widest">MERCOSUL</span>
          <span>BR</span>
        </div>
      ) : (
        <div className="h-3 border-b border-zinc-300" />
      )}
      <div className="flex h-[calc(100%-12px)] items-center justify-center">
        <span className="font-display text-[28px] font-extrabold leading-none tracking-[0.08em]">
          {formatado}
        </span>
      </div>
    </div>
  );
}

/**
 * Formata placa pra display visual: ABC-1234 (antiga) ou ABC1D23 (Mercosul).
 */
function formatarPlacaDisplay(s: string, padrao: 'MERCOSUL' | 'ANTIGA'): string {
  const n = normalizarPlaca(s);
  if (padrao === 'ANTIGA') {
    return n.length === 7 ? `${n.slice(0, 3)}-${n.slice(3)}` : n;
  }
  return n; // Mercosul é sem hífen
}

export function AdicionarVeiculoPage() {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const veioDoOnboarding = searchParams.get('onboarding') === '1';

  const [placa, setPlaca] = useState('');
  const [seqMarca, setSeqMarca] = useState<string>('');
  const [seqModelo, setSeqModelo] = useState<string>('');
  const [cor, setCor] = useState('');
  const [apelido, setApelido] = useState('');
  const [carregando, setCarregando] = useState(false);
  const [erroDetalhado, setErroDetalhado] = useState<string | null>(null);
  const [sucesso, setSucesso] = useState(false);

  // Marcas (1 query global, cacheada)
  const marcas = useQuery({
    queryKey: ['marcas-veiculo'],
    queryFn: async () => {
      const { data, error } = await sb
        .from('tab_marcas_veiculo')
        .select('seq_marca, nom_marca')
        .eq('ind_ativo', true)
        .order('num_ordem')
        .order('nom_marca');
      if (error) throw error;
      return (data ?? []) as Marca[];
    },
    staleTime: 60 * 60 * 1000, // catalogo global, cache 1h
  });

  // Modelos filtrados pela marca selecionada
  const modelos = useQuery({
    queryKey: ['modelos-veiculo', seqMarca],
    queryFn: async () => {
      if (!seqMarca) return [] as Modelo[];
      const { data, error } = await sb
        .from('tab_modelos_veiculo')
        .select('seq_modelo, seq_marca, nom_modelo')
        .eq('seq_marca', Number(seqMarca))
        .eq('ind_ativo', true)
        .order('nom_modelo');
      if (error) throw error;
      return (data ?? []) as Modelo[];
    },
    enabled: !!seqMarca,
    staleTime: 60 * 60 * 1000,
  });

  const padraoPlaca = useMemo(() => classificarPlaca(placa), [placa]);
  const placaNormalizada = useMemo(() => normalizarPlaca(placa), [placa]);
  const placaValida = padraoPlaca !== 'INVALIDA';

  const marcaSelecionada = marcas.data?.find((m) => String(m.seq_marca) === seqMarca);
  const modeloSelecionado = modelos.data?.find((m) => String(m.seq_modelo) === seqModelo);

  const formValido = !!(
    placaValida &&
    marcaSelecionada &&
    modeloSelecionado &&
    cor.trim().length > 0
  );

  async function adicionar(e: React.FormEvent) {
    e.preventDefault();
    setErroDetalhado(null);

    if (!formValido) {
      toast.error('Preencha todos os campos obrigatórios');
      return;
    }
    if (!user) {
      toast.error('Sessao não encontrada — faca login novamente');
      return;
    }

    setCarregando(true);
    try {
      // 1) Resolve cliente do user atual
      const { data: cli, error: errCli } = await supabase
        .from('tab_clientes')
        .select('seq_cliente, seq_tenant')
        .eq('cod_auth_user', user.id)
        .maybeSingle();

      if (errCli) throw errCli;
      if (!cli) {
        throw new Error(
          'Seu cadastro de cliente não foi encontrado. Faca logout e login novamente — se persistir, contate o suporte.',
        );
      }

      // 2) Insert. seq_tenant tem DEFAULT fn_tenant_atual() (migration 20260514110000),
      //    mas mandamos explicito por clareza/defesa em profundidade.
      const { error } = await supabase.from('tab_veiculos').insert({
        seq_tenant: cli.seq_tenant,
        seq_cliente: cli.seq_cliente,
        num_placa: placaNormalizada,
        cod_padrao_placa: padraoPlaca,
        nom_marca: marcaSelecionada!.nom_marca,
        nom_modelo: modeloSelecionado!.nom_modelo,
        des_cor: cor.trim(),
        cod_apelido: apelido.trim() || null,
      });

      if (error) throw error;

      // Se veio do fluxo de onboarding, marca o cliente como onboarded
      // (best-effort — falha silenciosa, hook useEnsureOnboarding retenta dps).
      if (veioDoOnboarding) {
        try {
          // eslint-disable-next-line @typescript-eslint/no-explicit-any
          const sbAny = supabase as any;
          await sbAny
            .from('tab_clientes')
            .update({ ind_onboarding_completo: true })
            .eq('seq_cliente', cli.seq_cliente);
        } catch {
          // segue
        }
      }

      // Toast + tela celebratória, auto-redirect em 1.6s
      toast.success('Veículo adicionado!', { description: 'Pronto pra ser lavado.' });
      setSucesso(true);
      setTimeout(() => navigate(veioDoOnboarding ? '/onboarding' : '/veiculos'), 1600);
    } catch (err) {
      const msg = mensagemErro(err);
      setErroDetalhado(msg);
      toast.error('Falha ao adicionar veículo', { description: msg });
      setCarregando(false);
    }
  }

  // ---------------------------------------------------------------------------
  // TELA CELEBRATÓRIA
  // ---------------------------------------------------------------------------
  if (sucesso) {
    return (
      <div className="bg-octa-hero relative flex min-h-[80vh] items-center justify-center overflow-hidden p-4">
        <motion.div
          aria-hidden
          className="octa-blob bg-octa-gradient pointer-events-none absolute -left-40 -top-40 h-[28rem] w-[28rem]"
          initial={{ opacity: 0, scale: 0.6 }}
          animate={{ opacity: 0.5, scale: 1 }}
          transition={{ duration: 1.2 }}
        />
        <motion.div
          aria-hidden
          className="octa-blob bg-octa-accent pointer-events-none absolute -bottom-32 -right-32 h-[24rem] w-[24rem]"
          style={{ animationDelay: '-4s' }}
          initial={{ opacity: 0, scale: 0.6 }}
          animate={{ opacity: 0.5, scale: 1 }}
          transition={{ duration: 1.2, delay: 0.15 }}
        />
        <motion.div
          className="relative z-10 w-full max-w-sm text-center"
          initial={{ opacity: 0, y: 20, scale: 0.9 }}
          animate={{ opacity: 1, y: 0, scale: 1 }}
          transition={{ duration: 0.6, ease: [0.34, 1.56, 0.64, 1] }}
        >
          <motion.div
            className="bg-octa-gradient shadow-octa-glow relative mx-auto flex h-24 w-24 items-center justify-center rounded-full ring-4 ring-white/40"
            initial={{ scale: 0 }}
            animate={{ scale: 1 }}
            transition={{ delay: 0.2, duration: 0.6, ease: [0.34, 1.56, 0.64, 1] }}
          >
            <CheckCircle2 className="h-12 w-12 text-white" />
            <motion.div
              aria-hidden
              className="absolute -right-2 -top-2"
              initial={{ scale: 0, rotate: -45 }}
              animate={{ scale: 1, rotate: 0 }}
              transition={{ delay: 0.5, duration: 0.5, ease: [0.34, 1.56, 0.64, 1] }}
            >
              <Sparkles className="text-primary h-7 w-7 drop-shadow-md" />
            </motion.div>
          </motion.div>
          <motion.h1
            className="text-display-lg mt-6 font-extrabold tracking-tight"
            initial={{ opacity: 0, y: 8 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.5 }}
          >
            Veículo adicionado
          </motion.h1>
          <motion.p
            className="text-muted-foreground mt-3 text-sm"
            initial={{ opacity: 0, y: 8 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.65 }}
          >
            <span className="font-bold tracking-wider">{placaNormalizada}</span>
            {marcaSelecionada && modeloSelecionado && (
              <>
                {' '}
                · {marcaSelecionada.nom_marca} {modeloSelecionado.nom_modelo}
              </>
            )}
            <br />
            já está pronto pra ser lavado.
          </motion.p>
          <motion.div
            className="mt-6 flex items-center justify-center gap-2 text-xs"
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            transition={{ delay: 0.85 }}
          >
            <Loader2 className="text-muted-foreground h-3 w-3 animate-spin" />
            <span className="text-muted-foreground">Indo pros seus veículos…</span>
          </motion.div>
        </motion.div>
      </div>
    );
  }

  // ---------------------------------------------------------------------------
  // FORM
  // ---------------------------------------------------------------------------
  return (
    <div className="space-y-5 p-4 pb-24">
      {/* Page header com back + ícone gradient */}
      <div className="octa-fade-in-up flex items-start gap-3 pt-1">
        <button
          type="button"
          onClick={() => navigate('/veiculos')}
          aria-label="Voltar para meus veículos"
          className="octa-tap text-muted-foreground hover:text-foreground border-border/40 bg-card/60 hover:bg-accent/60 mt-1 flex h-9 w-9 shrink-0 items-center justify-center rounded-xl border backdrop-blur-sm transition-colors"
        >
          <ArrowLeft className="h-4 w-4" />
        </button>
        <div className="flex-1 space-y-1">
          <p className="text-overline text-muted-foreground uppercase">Sua frota</p>
          <h1 className="text-display-md font-extrabold tracking-tight">Adicionar veículo</h1>
          <p className="text-muted-foreground text-sm">
            Em menos de um minuto, seu carro está pronto pra ser lavado.
          </p>
        </div>
        <div className="bg-octa-gradient shadow-octa-glow flex h-12 w-12 shrink-0 items-center justify-center rounded-2xl text-white">
          <CarFront className="h-6 w-6" />
        </div>
      </div>

      {/* Placa preview hero — mostra a placa visual em tempo real */}
      <motion.div
        initial={{ opacity: 0, y: 8 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ delay: 0.05, duration: 0.4 }}
      >
        <Card variant="glass" className="overflow-hidden">
          <CardContent className="space-y-3 p-5">
            <PlacaPreview texto={placa} padrao={padraoPlaca} />
            <AnimatePresence mode="wait">
              {placa.length === 0 ? (
                <motion.p
                  key="hint"
                  className="text-muted-foreground text-center text-xs"
                  initial={{ opacity: 0 }}
                  animate={{ opacity: 1 }}
                  exit={{ opacity: 0 }}
                >
                  Comece digitando a placa abaixo
                </motion.p>
              ) : placaValida ? (
                <motion.div
                  key="ok"
                  className="flex items-center justify-center gap-1.5 text-xs font-semibold text-emerald-600"
                  initial={{ opacity: 0, y: 4 }}
                  animate={{ opacity: 1, y: 0 }}
                  exit={{ opacity: 0 }}
                >
                  <CheckCircle2 className="h-3.5 w-3.5" />
                  Placa {padraoPlaca === 'MERCOSUL' ? 'Mercosul' : 'antiga'} válida
                </motion.div>
              ) : (
                <motion.p
                  key="err"
                  className="text-center text-xs font-semibold text-rose-600"
                  initial={{ opacity: 0, y: 4 }}
                  animate={{ opacity: 1, y: 0 }}
                  exit={{ opacity: 0 }}
                >
                  Formato inválido — use ABC-1234 ou ABC1D23
                </motion.p>
              )}
            </AnimatePresence>
          </CardContent>
        </Card>
      </motion.div>

      <motion.div
        initial={{ opacity: 0, y: 12 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ delay: 0.12, duration: 0.5 }}
      >
        <Card variant="default">
          <CardContent className="p-5">
            <form onSubmit={adicionar} className="space-y-5">
              {/* Placa input */}
              <div className="space-y-1.5">
                <Label htmlFor="placa" className="text-xs font-semibold uppercase tracking-wider">
                  Placa <span className="text-rose-500">*</span>
                </Label>
                <Input
                  id="placa"
                  value={placa}
                  onChange={(e) => setPlaca(e.target.value.toUpperCase())}
                  required
                  maxLength={8}
                  placeholder="ABC1D23"
                  className="h-12 rounded-xl text-lg font-extrabold uppercase tracking-[0.2em]"
                  autoComplete="off"
                  inputMode="text"
                />
              </div>

              {/* Marca + Modelo (cascata) */}
              <div className="grid gap-4 sm:grid-cols-2">
                <div className="space-y-1.5">
                  <Label className="text-xs font-semibold uppercase tracking-wider">
                    Marca <span className="text-rose-500">*</span>
                  </Label>
                  <Select
                    value={seqMarca}
                    onValueChange={(v) => {
                      setSeqMarca(v);
                      setSeqModelo(''); // reset modelo ao trocar marca
                    }}
                    disabled={marcas.isLoading}
                  >
                    <SelectTrigger className="h-12 rounded-xl">
                      <SelectValue placeholder={marcas.isLoading ? 'Carregando...' : 'Selecione'} />
                    </SelectTrigger>
                    <SelectContent>
                      {marcas.data?.map((m) => (
                        <SelectItem key={m.seq_marca} value={String(m.seq_marca)}>
                          {m.nom_marca}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>

                <div className="space-y-1.5">
                  <Label className="text-xs font-semibold uppercase tracking-wider">
                    Modelo <span className="text-rose-500">*</span>
                  </Label>
                  <Select
                    value={seqModelo}
                    onValueChange={setSeqModelo}
                    disabled={!seqMarca || modelos.isLoading}
                  >
                    <SelectTrigger className="h-12 rounded-xl">
                      <SelectValue
                        placeholder={
                          !seqMarca
                            ? 'Marca primeiro'
                            : modelos.isLoading
                              ? 'Carregando...'
                              : 'Selecione'
                        }
                      />
                    </SelectTrigger>
                    <SelectContent>
                      {modelos.data?.map((m) => (
                        <SelectItem key={m.seq_modelo} value={String(m.seq_modelo)}>
                          {m.nom_modelo}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                  {seqMarca && modelos.data?.length === 0 && !modelos.isLoading && (
                    <p className="text-muted-foreground text-xs">
                      Nenhum modelo cadastrado pra essa marca
                    </p>
                  )}
                </div>
              </div>

              {/* Cor — swatches visuais + input livre */}
              <div className="space-y-2">
                <Label htmlFor="cor" className="text-xs font-semibold uppercase tracking-wider">
                  <Palette className="mr-1 inline h-3 w-3" />
                  Cor <span className="text-rose-500">*</span>
                </Label>
                <div className="flex flex-wrap gap-2">
                  {CORES_POPULARES.map((c) => {
                    const ativo = cor === c.nome;
                    return (
                      <button
                        key={c.nome}
                        type="button"
                        onClick={() => setCor(c.nome)}
                        aria-pressed={ativo}
                        className={cn(
                          'octa-tap border-border/40 bg-card/40 group relative flex h-9 items-center gap-2 rounded-full border pl-1 pr-3 text-xs font-semibold transition-all',
                          ativo
                            ? 'border-primary shadow-octa-glow scale-105'
                            : 'hover:border-primary/40 hover:bg-accent/60',
                        )}
                      >
                        <span
                          className="ring-border/60 h-7 w-7 rounded-full ring-1"
                          style={{ background: c.bg }}
                          aria-hidden
                        />
                        {c.nome}
                        {ativo && <CheckCircle2 className="text-primary h-3.5 w-3.5" aria-hidden />}
                      </button>
                    );
                  })}
                </div>
                <Input
                  id="cor"
                  value={cor}
                  onChange={(e) => setCor(e.target.value)}
                  placeholder="ou digite outra cor"
                  className="h-11 rounded-xl"
                />
              </div>

              {/* Apelido (opcional) */}
              <div className="space-y-1.5">
                <Label htmlFor="apelido" className="text-xs font-semibold uppercase tracking-wider">
                  <Tag className="mr-1 inline h-3 w-3" />
                  Apelido{' '}
                  <span className="text-muted-foreground text-[10px] normal-case tracking-normal">
                    (opcional)
                  </span>
                </Label>
                <Input
                  id="apelido"
                  value={apelido}
                  onChange={(e) => setApelido(e.target.value)}
                  placeholder="Carro do trabalho, Carro da esposa..."
                  maxLength={40}
                  className="h-11 rounded-xl"
                />
              </div>

              {/* Erro detalhado */}
              <AnimatePresence>
                {erroDetalhado && (
                  <motion.div
                    initial={{ opacity: 0, height: 0 }}
                    animate={{ opacity: 1, height: 'auto' }}
                    exit={{ opacity: 0, height: 0 }}
                    className="overflow-hidden"
                  >
                    <div className="rounded-xl border border-rose-200 bg-rose-50/80 p-3 text-sm text-rose-700 dark:border-rose-900/40 dark:bg-rose-950/30 dark:text-rose-300">
                      <p className="font-semibold">Não foi possivel adicionar</p>
                      <p className="mt-1 text-xs">{erroDetalhado}</p>
                    </div>
                  </motion.div>
                )}
              </AnimatePresence>

              {/* Ações — submit hero brand-tinted */}
              <div className="flex flex-col-reverse gap-2 pt-2 sm:flex-row">
                <Button
                  type="button"
                  variant="outline"
                  onClick={() => navigate('/veiculos')}
                  disabled={carregando}
                  className="h-12 rounded-xl sm:flex-none"
                >
                  Cancelar
                </Button>
                <Button
                  type="submit"
                  variant="accent"
                  size="lg"
                  disabled={carregando || !formValido}
                  className="h-12 flex-1 rounded-xl text-base font-bold"
                >
                  {carregando ? (
                    <>
                      <Loader2 className="h-4 w-4 animate-spin" />
                      Salvando...
                    </>
                  ) : (
                    <>
                      <Sparkles className="h-4 w-4" />
                      Adicionar
                    </>
                  )}
                </Button>
              </div>
            </form>
          </CardContent>
        </Card>
      </motion.div>
    </div>
  );
}
